import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader, TextInput, Textarea, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react'
import { AdvertiserAvatar, AdvertiserSearch } from './AdvertiserSearch'
import { AddAdvertiserModal } from './AddAdvertiserModal'
import { FilesField, SourceSelector } from './SourceSelector'
import { Segmented } from '../v2/Segmented'
import { useWebsiteAvailability } from '../../hooks/useWebsiteAvailability'
import { IconSparklesTwo } from '../icons'
import { cx } from '../../utils/cx'
import css from '../../styles/EditorV2.module.css'
import classes from './GenerationForm.module.css'

/** The short label at the end of the website field: checking, reachable or not reachable. */
function WebsiteStatus({ status }) {
  const { t } = useTranslation()
  if (status === 'checking') {
    return (
      <span className={classes.websiteStatus}>
        <Loader size={12} color="gray" />
        {t('editorV2.generation.form.websiteChecking')}
      </span>
    )
  }
  if (status === 'reachable') {
    return (
      <span className={classes.websiteStatus} data-status="reachable">
        <IconCircleCheck size={14} stroke={2} />
        {t('editorV2.generation.form.websiteReachable')}
      </span>
    )
  }
  if (status === 'unreachable') {
    return (
      <span className={classes.websiteStatus} data-status="unreachable">
        <IconAlertCircle size={14} stroke={2} />
        {t('editorV2.generation.form.websiteUnreachable')}
      </span>
    )
  }
  return null
}

/** Clickout segment (Figma "clickout"): #F8F9FA track, 412×40, 16px labels, info tooltip. */
export function ClickoutControl({ value, onChange, disabled }) {
  const { t } = useTranslation()
  const tooltip = t('editorV2.generation.form.clickoutTooltip')
  return (
    <div className={classes.field}>
      <span className={css.label} style={{ marginBottom: 0 }}>{t('editorV2.generation.form.clickout')}</span>
      <Segmented
        ariaLabel={t('editorV2.generation.form.clickout')}
        size="lg"
        fill="light"
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={classes.clickout}
        options={[
          { value: 'own-website', label: t('editorV2.generation.form.ownWebsite') },
          {
            value: 'landing-page',
            label: t('editorV2.generation.form.instantLandingpage'),
            suffix: (
              <Tooltip
                label={tooltip}
                multiline
                w={252}
                position="top"
                classNames={{ tooltip: classes.tooltip }}
                events={{ hover: true, focus: true, touch: true }}
              >
                <span className={classes.infoIcon} aria-label={tooltip}>
                  <IconInfoCircle size={14} stroke={1.8} />
                </span>
              </Tooltip>
            ),
          },
        ]}
      />
    </div>
  )
}

function RecentAdvertisers({ form, apiBase, disabled }) {
  const { t } = useTranslation()
  if (!form.recent.length) return null
  return (
    <div className={classes.recent}>
      <span className={classes.recentLabel}>{t('editorV2.generation.form.recent')}</span>
      {form.recent.map((item) => (
        <button
          type="button"
          key={item.place_id}
          className={classes.chip}
          data-recent-chip="true"
          data-active={form.advertiser?.place_id === item.place_id ? 'true' : 'false'}
          disabled={disabled}
          onClick={() => form.selectAdvertiser(item)}
        >
          <AdvertiserAvatar place={item} apiBase={apiBase} size={18} />
          <span className={classes.chipName}>{item.name}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * "Ad Generation with AI" (Figma 84:4170 and Step 1 frames 421:95936 / 421:96484):
 *
 *   page — the Step 1 card: gradient band, "Campaign" (advertiser with recents, clickout),
 *          "Brief" (what to promote, source, files) and Start / Build without AI;
 *   chat — the stacked form inside the assistant (226:18740): advertiser, brief, source and
 *          files, started from the composer's Send button.
 *
 * The state lives in useGenerationForm so both variants edit the same inputs.
 */
export function GenerationForm({
  form,
  variant = 'page',
  apiBase,
  language,
  disabled = false,
  running = false,
  onStart,
  onBuildWithoutAi,
  onCancel,
  onBackToEditor,
  error,
  narrow = false,
}) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(null)
  const chat = variant === 'chat'
  const missingAdvertiser = form.attempted && !form.advertiser
  const ownWebsite = form.clickoutMode === 'own-website'
  const website = useWebsiteAvailability(apiBase, form.clickoutUrl, ownWebsite && !form.clickoutUrlError)
  const websiteError = form.clickoutUrlError
    || (website.status === 'unreachable' ? t('editorV2.generation.form.websiteUnreachableHelp') : undefined)

  const start = async () => {
    form.setAttempted(true)
    if (!form.advertiser || form.clickoutUrlError || disabled || running) return
    // A site that does not answer would only fail the generation minutes later.
    if (await website.ensure() === 'unreachable') return
    onStart?.()
  }
  const buildWithoutAi = () => {
    // "Own Website" needs the address the ad links to, with or without AI.
    if (form.clickoutUrlError) { form.setAttempted(true); return }
    onBuildWithoutAi?.()
  }

  const advertiser = (
    <div className={classes.fieldTight}>
      <AdvertiserSearch
        value={form.advertiser}
        onSelect={form.selectAdvertiser}
        onClear={() => form.selectAdvertiser(null)}
        onAddNew={(typed) => setAdding(typed || '')}
        addLabel={chat ? t('editorV2.generation.form.createNewAdvertiser') : t('editorV2.generation.form.addNewAdvertiser')}
        apiBase={apiBase}
        language={language}
        disabled={disabled || running}
        compact={narrow}
        error={missingAdvertiser ? t('editorV2.generation.form.chooseAdvertiser') : undefined}
      />
      <RecentAdvertisers form={form} apiBase={apiBase} disabled={disabled || running} />
    </div>
  )

  const brief = (
    <Textarea
      label={(
        <>
          {chat ? t('editorV2.generation.form.whatToAdvertise') : t('editorV2.generation.form.whatToPromote')}
          {' '}
          <span className={css.optional}>{t('editorV2.generation.form.optional')}</span>
        </>
      )}
      placeholder={t('editorV2.generation.form.briefPlaceholder')}
      autosize
      minRows={2}
      maxRows={8}
      maxLength={2000}
      value={form.briefing}
      onChange={(event) => form.setBriefing(event.currentTarget.value)}
      disabled={disabled || running}
      classNames={{ input: classes.textarea }}
    />
  )

  const modal = (
    <AddAdvertiserModal
      opened={adding !== null}
      initialName={adding || ''}
      onClose={() => setAdding(null)}
      onSubmit={(place) => {
        form.selectAdvertiser(place)
        setAdding(null)
      }}
    />
  )

  if (chat) {
    return (
      <div className={cx(classes.chat, narrow && classes.chatNarrow)}>
        {advertiser}
        {brief}
        <SourceSelector form={form} disabled={disabled || running} stacked={narrow} />
        {form.sourceKind !== 'files' && <FilesField form={form} disabled={disabled || running} />}
        {error && <p className={classes.error} role="alert">{error}</p>}
        {modal}
      </div>
    )
  }

  return (
    <section className={cx(css.card, classes.card)} aria-labelledby="studio-generation-title">
      <div className={classes.band}>
        <span className={classes.tile} aria-hidden="true"><IconSparklesTwo size={24} /></span>
        <span className={classes.bandText}>
          <span id="studio-generation-title" className={classes.bandTitle}>{t('editorV2.generation.form.title')}</span>
          <span className={classes.bandSubtitle}>
            {t('editorV2.generation.form.subtitle')}
          </span>
        </span>
      </div>

      <div className={classes.body}>
        <div className={classes.block}>
          <h3 className={css.sectionHeading}>{t('editorV2.generation.form.campaign')}</h3>
          <div className={classes.fields}>
            {advertiser}
            <div className={classes.fieldTight}>
              <ClickoutControl
                value={form.clickoutMode}
                onChange={form.setClickoutMode}
                disabled={disabled || running}
              />
              {form.clickoutMode === 'own-website' && (
                <TextInput
                  mt={8}
                  label={t('editorV2.generation.form.website')}
                  withAsterisk
                  type="url"
                  placeholder={t('editorV2.generation.form.websitePlaceholder')}
                  description={t('editorV2.generation.form.websiteHelp')}
                  value={form.clickoutUrl}
                  onChange={(event) => form.setClickoutUrl(event.currentTarget.value)}
                  error={form.attempted ? websiteError : undefined}
                  disabled={disabled || running}
                  rightSection={<WebsiteStatus status={website.status} />}
                  rightSectionWidth={website.status === 'idle' ? undefined : 150}
                  rightSectionPointerEvents="none"
                />
              )}
            </div>
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.block}>
          <h3 className={css.sectionHeading}>{t('editorV2.generation.form.brief')}</h3>
          <div className={classes.fields}>
            {brief}
            <SourceSelector form={form} disabled={disabled || running} />
            {form.sourceKind !== 'files' && <FilesField form={form} disabled={disabled || running} />}
          </div>
        </div>

        {error && <p className={classes.error} role="alert">{error}</p>}

        <div className={classes.divider} />

        <div className={classes.actions}>
          <button
            type="button"
            className={cx(css.button, css.buttonPrimary)}
            onClick={start}
            disabled={disabled || running}
            aria-busy={running}
          >
            {running && <Loader size={14} color="white" />}
            {t('editorV2.generation.form.start')}
            {!running && <IconSparklesTwo size={16} stroke={1.8} />}
          </button>
          <button
            type="button"
            className={css.button}
            onClick={buildWithoutAi}
            disabled={disabled || running}
          >
            {t('editorV2.generation.form.buildWithoutAi')}
          </button>
          {running && onCancel && (
            <button type="button" className={classes.cancel} onClick={onCancel}>
              {t('editorV2.generation.form.cancel')}
            </button>
          )}
          {/* Coming back from an existing ad: leave Step 1 with the ad unchanged. */}
          {!running && onBackToEditor && (
            <button type="button" className={classes.cancel} onClick={onBackToEditor}>
              {t('editorV2.generation.form.backToEditor')}
            </button>
          )}
        </div>
      </div>
      {modal}
    </section>
  )
}
